import React, { useCallback, useEffect, useRef } from 'react';
import { trpc } from '@/lib/trpc';
import { ActivityItem } from '@/pages/activities/components/ActivityItem';
import { Spinner } from '@heroui/react';
import { Segment } from '@heroui-pro/react';

type SegmentKind = 'team' | 'mine';

/**
 * 活动列表页面
 *
 * 特性：
 * - 使用 Segment 切换「团队活动 / 我的活动」（与我的邀请页一致的数据切换模式）
 * - 使用 Card + Badge 展示活动列表项（见 ActivityItem）
 * - 向上滚动时自动加载下一页（无限滚动）
 * - 点击卡片进入详情页
 */
export default function ActivitiesPage() {
  const [segment, setSegment] = React.useState<SegmentKind>('team');
  const teamObserverTargetRef = useRef<HTMLDivElement>(null);
  const mineObserverTargetRef = useRef<HTMLDivElement>(null);
  const [teamPage, setTeamPage] = React.useState(1);
  const [myPage, setMyPage] = React.useState(1);
  const [teamActivities, setTeamActivities] = React.useState<any[]>([]);
  const [myActivities, setMyActivities] = React.useState<any[]>([]);
  const [teamHasNextPage, setTeamHasNextPage] = React.useState(false);
  const [myHasNextPage, setMyHasNextPage] = React.useState(false);

  const {
    data: teamData,
    isLoading: isTeamLoading,
    isFetching: isTeamFetching,
    isError: isTeamError,
    error: teamError,
  } = trpc.activities.list.useQuery(
    {
      page: teamPage,
      pageSize: 10,
      sortBy: 'startTime' as const,
      sortOrder: 'desc' as const,
      status: 'published' as const,
    },
    { enabled: segment === 'team' }
  );

  const {
    data: myData,
    isLoading: isMyLoading,
    isFetching: isMyFetching,
    isError: isMyError,
    error: myError,
  } = trpc.activities.listMy.useQuery(
    {
      page: myPage,
      pageSize: 10,
      sortBy: 'startTime' as const,
      sortOrder: 'desc' as const,
    },
    { enabled: segment === 'mine' }
  );

  const isFetchingNextTeamPage = isTeamFetching && teamPage > 1;
  const isFetchingNextMyPage = isMyFetching && myPage > 1;

  const handleSegmentChange = useCallback((key: React.Key) => {
    const k = String(key);
    if (k === 'team' || k === 'mine') setSegment(k as SegmentKind);
  }, []);

  useEffect(() => {
    if (!teamData) return;
    const nextItems = teamData.items || [];
    setTeamHasNextPage(Boolean(teamData.hasNext));
    if (teamPage === 1) {
      setTeamActivities(nextItems);
      return;
    }
    setTeamActivities((prev) => {
      const seen = new Set(prev.map((item) => String(item.id)));
      const merged = [...prev];
      nextItems.forEach((item) => {
        const id = String(item.id);
        if (!seen.has(id)) {
          seen.add(id);
          merged.push(item);
        }
      });
      return merged;
    });
  }, [teamData, teamPage]);

  useEffect(() => {
    if (!myData) return;
    const nextItems = myData.items || [];
    setMyHasNextPage(Boolean(myData.hasNext));
    if (myPage === 1) {
      setMyActivities(nextItems);
      return;
    }
    setMyActivities((prev) => {
      const seen = new Set(prev.map((item) => String(item.id)));
      const merged = [...prev];
      nextItems.forEach((item) => {
        const id = String(item.id);
        if (!seen.has(id)) {
          seen.add(id);
          merged.push(item);
        }
      });
      return merged;
    });
  }, [myData, myPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (segment !== 'team') return;
        if (target.isIntersecting && teamHasNextPage && !isFetchingNextTeamPage) {
          setTeamPage((prev) => prev + 1);
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    const currentTarget = teamObserverTargetRef.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [teamHasNextPage, isFetchingNextTeamPage, segment]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (segment !== 'mine') return;
        if (target.isIntersecting && myHasNextPage && !isFetchingNextMyPage) {
          setMyPage((prev) => prev + 1);
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    const currentTarget = mineObserverTargetRef.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [myHasNextPage, isFetchingNextMyPage, segment]);

  const isTeam = segment === 'team';
  const isLoading = isTeam ? isTeamLoading : isMyLoading;
  const isError = isTeam ? isTeamError : isMyError;
  const error = isTeam ? teamError : myError;
  const activities = isTeam ? teamActivities : myActivities;
  const hasNextPage = isTeam ? teamHasNextPage : myHasNextPage;
  const isFetchingNextPage = isTeam ? isFetchingNextTeamPage : isFetchingNextMyPage;
  const sentinelRef = isTeam ? teamObserverTargetRef : mineObserverTargetRef;
  const emptyMessage = isTeam ? '暂无团队活动' : '您还没有发起过活动';

  return (
    <div className="w-full p-4">
      <div className="flex items-center justify-center mb-4">
        <Segment selectedKey={segment} onSelectionChange={handleSegmentChange}>
          <Segment.Item id="team">
            <Segment.Separator />
            团队活动
          </Segment.Item>
          <Segment.Item id="mine">
            <Segment.Separator />
            我的活动
          </Segment.Item>
        </Segment>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner className="w-8 h-8" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
          <p className="text-danger mb-2">加载失败</p>
          <p className="text-sm text-default-500 text-center">
            {error?.message || '未知错误'}
          </p>
        </div>
      ) : activities.length === 0 ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-default-500">{emptyMessage}</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4">
            {activities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </div>
          <div ref={sentinelRef} className="h-20 flex items-center justify-center">
            {isFetchingNextPage && <Spinner className="w-6 h-6" />}
            {!hasNextPage && activities.length > 0 && (
              <p className="text-sm text-default-500">没有更多了</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
